angular.module('Itao.IndicatorMap').factory('d3', function () {
  'use strict';

  if (!angular.isDefined(d3) || !angular.isObject(d3)) {
    throw 'd3 has to be loaded for D3Service';
  }

  /**
   * Helperfunction for creating accessor functions
   * @param {String} field the property name to return, if undefined the datum will be returned
   * @returns {Function}
   */
  var accessor = function (field) {
    return function (datum) {
      return typeof(field) === 'undefined' ? datum : datum[field];
    };
  };

  /**
   * Helperfunction for composing functions, commonly used for composing accessor functions with scale functions
   * @param {Function} g the outer function
   * @param {Function} h the inner function
   * @returns {Function}
   */
  var compose = function (g, h) {
    return function () {
      return g(h.apply(null, arguments));
    };
  };

  /**
   * German number and time formatting
   * @type {{numberFormat, timeFormat}}
   */
  var germanFormat = d3.locale({
    'decimal': ',',
    'thousands': '.',
    'grouping': [3],
    'currency': ['€', ''],
    'dateTime': '%a %b %e %X %Y',
    'date': '%d.%m.%Y',
    'time': '%H:%M:%S',
    'periods': ['AM', 'PM'],
    'days': ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
    'shortDays': ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    'months': ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    'shortMonths': ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
  });

  var gridColor = '#ddd';
  var gridWidth = '1px';

  /**
   * Generatorfunction for grid generation
   *
   * @param scale
   * @param x1
   * @param x2
   * @param y1
   * @param y2
   * @param tickCount
   * @param duration
   * @returns {Function}
   */
  var addGridLines = function (scale, x1, x2, y1, y2, tickCount, duration) {

    var applyLinePosition = function (selection) {
      selection.attr({
        'x1': x1 === null ? function (d) {
          return scale(d);
        } : x1,
        'x2': x2 === null ? function (d) {
          return scale(d);
        } : x2,
        'y1': y1 === null ? function (d) {
          return scale(d);
        } : y1,
        'y2': y2 === null ? function (d) {
          return scale(d);
        } : y2
      });
    };

    return function (selection) {
      var horizontalGridGroup = selection.selectAll(".horizontalGridGroup").data([1]);
      horizontalGridGroup.enter().append('g').attr({
        'class': 'horizontalGridGroup',
        'fill': 'none',
        'shape-rendering': 'crispEdges',
        'stroke': gridColor,
        'stroke-width': gridWidth
      });

      var horizontalGrid = horizontalGridGroup.selectAll("line.horizontalGrid").data(scale.ticks(tickCount), accessor());
      horizontalGrid.enter()
        .append("line")
        .attr('class', 'horizontalGrid')
        .call(applyLinePosition);

      horizontalGrid.transition().duration(duration)
        .call(applyLinePosition);

      horizontalGrid.exit().remove();

      return this;
    }
  };

  /**
   * Add horizontal grid lines to a diagram
   *
   * use like this:
   * g.call(addHorizontalGridLines(...))
   *
   * @param yScale
   * @param x1
   * @param x2
   * @param tickCount
   * @param duration
   * @returns {Function}
   */
  var addHorizontalGridLines = function (yScale, x1, x2, tickCount, duration) {
    return addGridLines(yScale, x1, x2, null, null, tickCount, duration);
  };

  /**
   * Add vertical grid lines to a diagram
   *
   * use like this:
   * g.call(addVerticalGridLines(...))
   *
   * @param xScale
   * @param y1
   * @param y2
   * @param tickCount
   * @param duration
   * @returns {Function}
   */
  var addVerticalGridLines = function (xScale, y1, y2, tickCount, duration) {
    return addGridLines(xScale, null, null, y1, y2, tickCount, duration);
  };

  /**
   * Add highlight lines to diagram
   *
   * use like this:
   * g.call(addHighlightLines(...))
   *
   * @param values
   * @param y
   * @param width
   * @param duration
   * @param minMax
   * @returns {Function}
   */
  var addHorizontalHighlightLines = function (values, y, width, duration, minMax) {
    return function (selection) {
      if (angular.isArray(values) && values.length > 0) {
        var redLineInitialY = y(0);
        var cssClass = minMax ? 'highlightLine highlightLine--min-max' : 'highlightLine';

        var redLine = selection.selectAll('.highlightLine')
          .data(values);

        redLine.enter().append('line')
          .attr('class', cssClass)
          .style('stroke-width', '4')
          .style('shape-rendering', 'crispEdges')
          .attr('pointer-events', 'none')
          .attr('x1', 0)
          .attr('y1', redLineInitialY)
          .attr('x2', width)
          .attr('y2', redLineInitialY);

        redLine.transition().duration(duration)
          .attr('x2', width)
          .attr('y1', function (d) {
            return y(d);
          })
          .attr('y2', function (d) {
            return y(d);
          });

        redLine.exit().remove();
      }
    };
  };

  /**
   * Add highlight lines to diagram
   *
   * use like this:
   * g.call(addHighlightLines(...))
   *
   * @param values
   * @param y
   * @param x
   * @param width
   * @param duration
   * @returns {Function}
   */
  var addHorizontalHighlightArea = function (values, y, x, width, duration) {
    return function (selection) {
      if (angular.isArray(values) && values.length > 0) {
        var initialY = y(0),
          initialX = x(0);

        var redLine = selection.selectAll('.highlightArea')
          .data(values);

        redLine.enter().append('rect')
          .attr('class', 'highlightArea')
          .attr('width', 0)
          .attr('x', initialX)
          .attr('y', initialY)
          .attr('height', 0);

        redLine.transition().duration(duration)
          .attr('y', function (d) {
            return y(Math.max(0, d));
          })

          .attr('width', width)
          .attr('height', function (d) {
            return Math.abs(y(0) - y(d));
          });

        redLine.exit().remove();
      } else {
        selection.selectAll('.highlightArea')
          .remove();
      }
    };
  };

  /**
   * add min-max-highlight-area to diagram
   *
   * use like this:
   * g.call(addHighlightLines(...))
   *
   * @param minMax
   * @param y
   * @param x
   * @param width
   * @param duration
   * @returns {Function}
   */
  var addHorizontalMinMaxHighlightArea = function (minMax, y, x, width, duration) {
    return function (selection) {
      if (angular.isArray(minMax) && minMax.length > 0) {
        var initialY = y(0),
          initialX = x(0);

        var redLine = selection.selectAll('.highlightArea')
          .data([minMax]);

        redLine.enter().append('rect')
          .attr('class', 'highlightArea highlightArea--min-max')
          .attr('width', 0)
          .attr('x', initialX)
          .attr('y', initialY)
          .attr('height', 0);

        redLine.transition().duration(duration)
          .attr('y', 0)

          .attr('width', width)
          .attr('height', function (d) {
            return Math.abs(y(d[1]));
          });

        redLine.exit().remove();
      } else {
        selection.selectAll('.highlightArea')
          .remove();
      }
    };
  };

  /**
   * Add highlight lines to diagram
   *
   * use like this:
   * g.call(addHighlightLines(...))
   *
   * @param values
   * @param y
   * @param x
   * @param height
   * @param duration
   * @returns {Function}
   */
  var addVerticalHighlightArea = function (values, y, x, height, duration) {
    return function (selection) {
      if (angular.isArray(values) && values.length > 0) {
        var initialY = y(0),
          initialX = x(0);

        var redLine = selection.selectAll('.highlightArea')
          .data(values);

        redLine.enter().append('rect')
          .attr('class', 'highlightArea')
          .attr('width', 0)
          .attr('x', initialX)
          .attr('y', initialY)
          .attr('height', 0);

        redLine.transition().duration(duration)
          .attr('x', function (d) {
            return x(Math.min(0, d));
          })

          .attr('height', height)
          .attr('width', function (d) {
            return Math.abs(x(0) - x(d));
          });

        redLine.exit().remove();
      }
    }
  };

  /**
   * Add highlight lines to diagram
   *
   * use like this:
   * g.call(addHighlightLines(...))
   *
   * @param values
   * @param x
   * @param height
   * @param duration
   * @returns {Function}
   */
  var addVerticalHighlightLines = function (values, x, height, duration) {
    return function (selection) {
      if (angular.isArray(values) && values.length > 0) {
        var redLineInitialX = x(0);

        var redLine = selection.selectAll('.highlightLine')
          .data(values);

        redLine.enter().append('line')
          .attr('class', 'highlightLine')
          .style('stroke-width', '4')
          .style('shape-rendering', 'crispEdges')
          .attr('pointer-events', 'none')
          .attr('y1', 0)
          .attr('x1', redLineInitialX)
          .attr('y2', height)
          .attr('x2', redLineInitialX);

        redLine.transition().duration(duration)
          .attr('y2', height)
          .attr('x1', function (d) {
            return x(d);
          })
          .attr('x2', function (d) {
            return x(d);
          });

        redLine.exit().remove();
      }
    }
  };

  /**
   * The colors to display the indicators in
   *
   * @type {string[]}
   */
  var indicatorColors = [
    '#1b9e77',
    '#d95f02',
    '#7570b3',
    '#e7298a',
    '#66a61e',
    '#e6ab02',
    '#a6761d',
    '#666666'
  ];

  var buildColorFunction = function (colors) {
    return function (number) {
      return colors[number % colors.length];
    };
  };

  var indicatorColorAt = buildColorFunction(indicatorColors);

  //store commonly used d3 helper functions under hyperion namespace
  d3.hyperion = {
    addHorizontalHighlightLines: addHorizontalHighlightLines,
    addHorizontalHighlightArea: addHorizontalHighlightArea,
    addHorizontalMinMaxHighlightArea: addHorizontalMinMaxHighlightArea,
    addVerticalHighlightArea: addVerticalHighlightArea,
    addVerticalHighlightLines: addVerticalHighlightLines,
    accessor: accessor,
    compose: compose,
    germanFormat: germanFormat,
    indicatorColors: indicatorColors,
    buildColorFunction: buildColorFunction,
    indicatorColorAt: indicatorColorAt,
    addHorizontalGridLines: addHorizontalGridLines,
    addVerticalGridLines: addVerticalGridLines
  };

  return d3;
});
